import { FC } from 'react';
import { MetaTitle } from 'src/components/seo/MetaTitle';
import { publicUrl } from 'vendor/utils/publicUrl';
import { Person, EducationEvent } from 'schema-dts';
import { LDJSON } from 'src/components/seo/LDJSON';
import { route } from 'src/services/route';
import { MetaCanonical } from 'src/components/seo/MetaCanonical';
import { MetaImage } from 'src/components/seo/MetaImage';
import { MetaDescription } from 'src/components/seo/MetaDescription';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { useFlattenEvent } from 'src/modules/events/hooks/useFlattenEvent';
import { useTranslate } from 'react-t7e';
import { Events } from 'src/graphql/schema.graphql';
import { getDirectusImageAssetUrl } from 'src/services/directus/common';

export interface EventPageSEOProps {
  event: Events;
}

export const EventPageSEO: FC<EventPageSEOProps> = ({ event }) => {
  const flatEvent = useFlattenEvent(event);

  const { _ } = useTranslate();
  const translateItem = useTranslateItem();

  const canonicalUrl = publicUrl(route.event(event).page());
  const imageUrl = flatEvent.seoImage
    ? publicUrl(
        getDirectusImageAssetUrl(flatEvent.seoImage.id, {
          width: 1200,
          height: 630,
          fit: 'cover',
          format: 'jpg',
        }),
      )
    : undefined;

  const title = flatEvent.type?.name
    ? `${flatEvent.type?.name} ${flatEvent.name}`
    : flatEvent.name;

  const description = flatEvent.excerpt ?? flatEvent.type?.excerpt;

  const meta: EducationEvent = {
    '@type': 'EducationEvent',
    name: title,
    description,
    startDate: flatEvent.startDate,
    endDate: flatEvent.endDate,
    url: canonicalUrl,
    image: imageUrl,
    performers: flatEvent.speakers
      ? flatEvent.speakers
          .map((t) => t.people_id)
          .map(translateItem)
          .map(
            (speaker): Person => ({
              '@type': 'Person',
              jobTitle: [speaker.degree, speaker.title].filter((v) => v),
              image: speaker.portrait
                ? publicUrl(getDirectusImageAssetUrl(speaker.portrait.id))
                : undefined,
            }),
          )
      : [],
  };

  return (
    <>
      <MetaTitle>
        {_('Events - {eventName}', undefined, {
          eventName: title,
        })}
      </MetaTitle>
      <MetaDescription>
        {_('Neauvia events, webinars, live streaming. {excerpt}', undefined, {
          excerpt: description,
        })}
      </MetaDescription>
      {imageUrl && <MetaImage src={imageUrl} />}
      <MetaCanonical href={canonicalUrl} />
      <LDJSON data={meta} />
    </>
  );
};
